import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, FileText, Download, Mail, Network } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { documents, publications, bookSeries } from '../data/mockData';

const REQUEST_PAPER =
  'mailto:contact@salartechnologies.com?subject=Salar%20Graph%20Research%20Strategy%20Request&body=Hello%20SALAR%20Technologies%20LLC%2C%0A%0AI%20would%20like%20to%20request%20the%20public-safe%20Salar%20Graph%20research%20strategy.%0A%0AName%3A%0AOrganization%3A%0AResearch%20or%20collaboration%20interest%3A%0A%0AThank%20you.';

const Resources = () => {
  return (
    <div className="min-h-screen bg-blue-950/20">
      <Helmet>
        <title>Resources | SALAR Technologies — Capability Statements & Briefs</title>
        <meta name="description" content="Capability statements, quad charts, technical briefs, patent portfolio overviews, line cards, and corporate data sheets — public-safe SALAR Technologies documents for prime contractors and program offices." />
        <meta property="og:title" content="SALAR Resources & Documents" />
        <meta property="og:description" content="Government Capability Statement, Quad Chart, Tech Briefs, Patent Overview, Line Card, Corporate Data Sheet, SBIR Performance Benchmark Report, and the SALAR research book series." />
        <meta property="og:url" content="https://www.salartechnologies.com/resources" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.salartechnologies.com/resources" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <section
        className="text-white py-20"
        style={{
          backgroundImage: "linear-gradient(rgba(10,22,40,0.65), rgba(10,22,40,0.9)), url('/img/tech_closeup.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="resources-hero"
      >
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <BookOpen className="w-20 h-20 mx-auto mb-6 text-slate-300" />
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">Resources & Publications</h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Research briefs, capability statements, and technical publications demonstrating SALAR's
              engineering-first approach to defense electronics and supply chain resilience.
            </p>
          </div>
        </div>
      </section>

      <section className="py-10" style={{ background: '#0F1A2B' }} data-testid="resources-divider-section">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="rounded-xl overflow-hidden border border-cyan-400/20" style={{ boxShadow: '0 6px 24px rgba(0,0,0,0.3)' }}>
              <img
                src="/img/salar_whitepaper_divider.webp"
                alt="Abstract wafer-to-board flow visual representing semiconductor packaging and electronics integration."
                loading="lazy"
                className="block w-full h-auto"
                style={{ maxHeight: 360, objectFit: 'cover', objectPosition: 'center' }}
                data-testid="resources-divider-image"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#081423] border-y border-cyan-400/20" data-testid="salar-graph-feature">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto rounded-3xl border border-cyan-400/30 bg-gradient-to-br from-[#0d2b47] via-[#0c2038] to-[#071120] p-7 md:p-10 grid lg:grid-cols-[auto_1fr] gap-7 items-center">
            <div className="w-20 h-20 rounded-2xl border border-cyan-400/35 bg-cyan-400/10 flex items-center justify-center">
              <Network className="text-cyan-300" size={38} />
            </div>
            <div>
              <Badge variant="outline" className="border-cyan-400/40 text-cyan-200 mb-3">FEATURED RESEARCH INITIATIVE</Badge>
              <h2 className="text-3xl font-bold text-white">Salar Graph</h2>
              <p className="mt-3 text-blue-100 leading-relaxed max-w-3xl">
                Explore SALAR's public-safe research initiative for AI-assisted reliability, semiconductor materials discovery, leadership-scale graph analytics, uncertainty scoring, reproducible evidence, and accountable human decision support.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Button asChild className="bg-cyan-500 hover:bg-cyan-400 text-[#071120] font-bold">
                  <Link to="/salar-graph">Explore Salar Graph <ArrowRight size={17} className="ml-2" /></Link>
                </Button>
                <Button asChild variant="outline" className="border-blue-300/50 text-blue-100 hover:bg-blue-500/15">
                  <a href={REQUEST_PAPER}><Mail size={17} className="mr-2" /> Request research strategy</a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-blue-900/10">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-white mb-12 text-center">Key Documents</h2>
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {documents.map((doc) => (
              <Card key={doc.id} className="border-[#1e3a5f]/20 hover:shadow-xl hover:shadow-cyan-500/10 transition-all duration-300">
                <CardHeader>
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-lg flex items-center justify-center mb-4">
                    <FileText className="text-white" size={28} />
                  </div>
                  <CardTitle className="text-xl text-white">{doc.title}</CardTitle>
                  <CardDescription className="text-blue-200">{doc.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between mb-4">
                    <Badge variant="outline" className="border-blue-400 text-blue-200">{doc.fileType}</Badge>
                    {doc.size && <span className="text-sm text-blue-300">{doc.size}</span>}
                  </div>
                  <a href={doc.url} target="_blank" rel="noopener noreferrer">
                    <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                      <Download className="mr-2" size={18} />
                      Download PDF
                    </Button>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-blue-950/20">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-white mb-4 text-center">Defense Electronics Book Series</h2>
          <p className="text-center text-blue-200 mb-12 max-w-3xl mx-auto">
            Six-volume research series covering critical topics in defense electronics supply chain sovereignty,
            counterfeit detection, rare earth dependencies, and manufacturing resilience.
          </p>
          <div className="max-w-6xl mx-auto space-y-8">
            {bookSeries.filter(book => book.available).map((book) => (
              <Card key={book.id} className="border-[#1e3a5f]/20">
                <CardHeader>
                  <div className="flex items-start justify-between flex-wrap gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Badge className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white">{book.seriesNumber}</Badge>
                        <CardTitle className="text-2xl text-white">{book.title}</CardTitle>
                      </div>
                      <p className="text-cyan-400 font-semibold mb-3">{book.subtitle}</p>
                      {book.tagline && (
                        <p className="text-blue-300 text-sm italic mb-3 leading-relaxed" data-testid={`book-tagline-${book.id}`}>{book.tagline}</p>
                      )}
                      <CardDescription className="text-blue-200 text-base">{book.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-3">
                    {book.pdfUrl && book.available && (
                      <a href={book.pdfUrl} target="_blank" rel="noopener noreferrer">
                        <Button className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white">
                          <Download className="mr-2" size={18} />
                          Download Overview
                        </Button>
                      </a>
                    )}
                    <a href="/contact">
                      <Button variant="outline" className="border-blue-400 text-blue-200 hover:bg-blue-400 hover:text-white">
                        <FileText className="mr-2" size={18} />
                        Request Access to Full Publication
                      </Button>
                    </a>
                  </div>
                  {book.available && book.pdfUrl && (
                    <p className="text-sm text-blue-300 mt-4">Public overview available for download. Full publication with detailed technical specifications available through appropriate partnership or government engagement channels.</p>
                  )}
                  {!book.available && (
                    <p className="text-sm text-blue-300 mt-4">This comprehensive publication is available through appropriate partnership, collaboration, or government engagement channels only.</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-blue-900/10">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Card className="border-[#1e3a5f] bg-blue-950/30 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white">Access to Additional Resources</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-lg text-blue-100 leading-relaxed mb-6">
                  Detailed technical specifications, proprietary methodologies, and sensitive implementation information are available only through appropriate partnership, collaboration, or government engagement channels with proper confidentiality protections in place.
                </p>
                <p className="text-blue-200">Contact SALAR Technologies to discuss access to restricted technical resources.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Resources;
